import { useLocation, useNavigate } from "react-router";

import { Image } from "@components/Image";
import { Stack } from "@components/Stack";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/Tooltip";
import { Typography } from "@components/Typography";

import { useVrVideoHistoryQuery } from "@storage/queries/gallery";
import { usePostListQuery } from "@storage/queries/post";

import { cn } from "@lib/utils";

import { ICON_PATHS } from "@configs/icons";
import { ROUTES } from "@configs/routes";

type NavItemConfig = {
	pathname: string;
	label: string;
	key: string;
	iconSrc: string;
	iconAlt: string;
	showEmptyPostsBadge?: boolean;
	showEmptyVrVideosBadge?: boolean;
};

const NAV_CONFIG: NavItemConfig[] = [
	{ pathname: ROUTES.HOME, label: "Chat", key: "10", iconSrc: ICON_PATHS.CHAT, iconAlt: "chat" },
	{
		pathname: ROUTES.SHOW_SETTINGS,
		label: "Settings",
		key: "20",
		iconSrc: ICON_PATHS.CONFIG,
		iconAlt: "showsettings",
	},
	{
		pathname: ROUTES.POSTS,
		label: "Posts",
		key: "30",
		iconSrc: ICON_PATHS.POSTS,
		iconAlt: "posts",
		showEmptyPostsBadge: true,
	},
	{
		pathname: ROUTES.VR_VIDEO,
		label: "VR Videos",
		key: "40",
		iconSrc: ICON_PATHS.VE_VIDEOS,
		iconAlt: "vr_videos",
		showEmptyVrVideosBadge: true,
	},
	{
		pathname: ROUTES.BLACKLIST,
		label: "Blacklist",
		key: "50",
		iconSrc: ICON_PATHS.BLOCKED,
		iconAlt: "blacklist",
	},
];

const NavIcon = ({
	src,
	alt,
	isActive,
	className,
}: {
	src: string;
	alt: string;
	isActive: boolean;
	className?: string;
}) => (
	<Image
		src={src}
		alt={alt}
		style={{ fontSize: "20px", width: "100%" }}
		className={cn(isActive ? "opacity-100" : "opacity-60", className)}
	/>
);

export const AttentionBadge = ({ className }: { className?: string }) => (
	<div className="absolute top-0.5 right-px text-ground opacity-100 group-hover:text-block">
		<Image src={ICON_PATHS.ATTENTION_RED} alt="attention" className={cn("h-4 w-4", className)} />
	</div>
);

const SidebarLogo = ({ onClick }: { onClick: () => void }) => (
	<div
		onClick={onClick}
		className="relative mx-auto mb-5 box-border flex h-10 w-10 cursor-pointer items-center overflow-hidden"
	>
		<Image src={ICON_PATHS.LOGO_FAVICON} alt="logo" className="mx-auto block" onClick={onClick} />
	</div>
);

const MenuItem = ({ item, icon }: { item: NavItemConfig; icon: React.ReactNode }) => {
	const navigate = useNavigate();

	return (
		<Tooltip key={item.key}>
			<TooltipTrigger>
				<div
					onClick={() => navigate(item.pathname)}
					className="mx-auto block h-10 w-10 cursor-pointer rounded-lg hover:bg-block"
				>
					{icon}
				</div>
			</TooltipTrigger>
			<TooltipContent side="right" sideOffset={15}>
				<Typography type="span" padding="none" size="sm">
					{item.label}
				</Typography>
			</TooltipContent>
		</Tooltip>
	);
};

export const SidebarLeft = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const postListQuery = usePostListQuery();
	const vrVideoHistoryQuery = useVrVideoHistoryQuery(null);

	const handleLogoClick = () => navigate(ROUTES.HOME);

	const shouldShowAttentionBadge = (item: NavItemConfig) => {
		if (item.showEmptyPostsBadge) {
			return postListQuery.isSuccess && postListQuery.data.posts.length === 0;
		}

		if (item.showEmptyVrVideosBadge) {
			return vrVideoHistoryQuery.isSuccess && vrVideoHistoryQuery.data.pagination.total_items_count === 0;
		}

		return false;
	};

	const renderNavIcon = (item: NavItemConfig) => {
		const isActive = location.pathname === item.pathname;
		const icon = <NavIcon src={item.iconSrc} alt={item.iconAlt} isActive={isActive} />;
		const showBadge = shouldShowAttentionBadge(item);

		if (showBadge) {
			return (
				<div className="group relative">
					{icon}
					<AttentionBadge />
				</div>
			);
		}

		return icon;
	};

	return (
		<div className="mb-0 w-full max-w-16 min-w-16 shrink-0 bg-ground px-3 pt-3">
			<SidebarLogo onClick={handleLogoClick} />
			<Stack spacing={12}>
				{NAV_CONFIG.map((item) => (
					<MenuItem key={item.key} item={item} icon={renderNavIcon(item)} />
				))}
			</Stack>
		</div>
	);
};
